<template>
  <div class="app-container">
    <div class="top-controls">
      <!-- Logo -->
      <div class="logo-container">
        <img src="/images/logo.png" alt="Logo" class="logo-image" />
      </div>
      
      <!-- 切换模式选择器 - 左侧 -->
      <div class="left-controls">
        <div class="mode-selector">
          <div class="selector-tabs">
            <div 
              class="tab" 
              :class="{ active: legendMode === 'priority' }"
              @click="setActiveTab('priority')"
            >
              Priority
            </div>
            <div 
              class="tab" 
              :class="{ active: legendMode === 'category' }"
              @click="setActiveTab('category')"
            >
            Explore
            </div>
          </div>
        </div>
      </div>
      
      <!-- 平台标题 - 中间 -->
      <h1 class="platform-title">Integrated Decision Support Platform for Urban Blue-Green Space Sustainability</h1>
      
      <!-- 右侧控件组 -->
      <div class="right-controls">
        <!-- 搜索区域 -->
        <div class="search-container">
          <div class="search-box">
            <input
              type="text"
              placeholder="Search a city or code"
              v-model="searchTerm"
              @input="handleInputChange"
              @keydown="handleKeyDown"
            />
            <button v-if="searchTerm" class="clear-button" @click="clearSearch">×</button>
            <button @click="handleSearch">Search</button>
          </div>

          <!-- 搜索建议下拉框 -->
          <div v-if="suggestions.length > 0" class="suggestions-dropdown">
            <div
              v-for="(item, index) in suggestions"
              :key="item.properties.gb?.slice(-6) || index"
              :class="['suggestion-item', { highlighted: index === highlightedIndex }]"
              @click="() => handleSuggestionClick(item)"
            >
              <div>
                <!-- 匹配文本高亮显示 -->
                <template v-if="matchName(item).match">
                  {{ matchName(item).before }}
                  <span class="matching-text">{{ matchName(item).match }}</span>
                  {{ matchName(item).after }}
                </template>
                <template v-else>
                  {{ item.properties.Eng_name || '' }}
                </template>
                <span class="city-code">({{ item.properties.gb?.slice(-6) || '' }})</span>
              </div>
            </div>
          </div>
        </div>
        
        <!-- A-E模式下的SSP选择器 -->
        <div v-if="legendMode === 'category'" class="ssp-selector">
          <select v-model="selectedSSPForAE" @change="handleSSPChangeForAE" class="ssp-select">
            <option value="base">Base Period</option>
            <option value="SSP1">SSP1</option>
            <option value="SSP2">SSP2</option>
            <option value="SSP3">SSP3</option>
            <option value="SSP4">SSP4</option>
            <option value="SSP5">SSP5</option>
          </select>
        </div>

        <!-- 年份选择器 -->
        <div class="year-selector" v-if="legendMode === 'category'">
          <select v-model="currentSelectedValue" @change="handleSSPChange" class="year-select">
            <template v-if="selectedSSPForAE === 'base'">
              <option value="2000">2000</option>
              <option value="2005">2005</option>
              <option value="2010">2010</option>
              <option value="2015">2015</option>
              <option value="2020">2020</option>
            </template>
            <template v-else>
              <option value="2030">2030</option>
              <option value="2040">2040</option>
              <option value="2050">2050</option>
              <option value="2060">2060</option>
              <option value="2070">2070</option>
              <option value="2080">2080</option>
              <option value="2090">2090</option>
              <option value="2100">2100</option>
            </template>
          </select>
        </div>

        <!-- Priority模式的SSP选择器 -->
        <div v-if="legendMode === 'priority'" class="ssp-selector">
          <select v-model="currentSelectedValue" @change="handleSSPChange" class="ssp-select">
            <option v-for="option in sspOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <!-- 地图容器 -->
    <div class="map-container">
      <LMap
        ref="mapRef"
        :center="[35.8617, 104.1954]"
        :zoom="4"
        :options="{ doubleClickZoom: false }"
        @ready="onMapReady"
        class="map"
      >
        <LTileLayer
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png"
          attribution="&copy; <a href='https://www.openstreetmap.org/copyright'>OpenStreetMap</a>"
        />
        <LGeoJson
          v-if="chinaGeoData"
          :geojson="chinaGeoData"
          ref="geoJsonLayerRef"
          :options="geoJsonOptions"
          @ready="onGeoJsonReady"
        />
      </LMap>
      
      <!-- 介绍面板 -->
      <IntroPanel />
      
      <!-- Priority图例 -->
      <div class="left-legend">
        <div class="legend-header">
          <div class="legend-title">Legend</div>
          <div class="legend-info-icon" @click="showInfoModal = true">ⓘ</div>
        </div>
        
        <!-- Priority值图例 -->
        <div v-if="legendMode === 'priority'" class="legend-items">
          <!-- Two column layout for negative and positive -->
          <div class="two-column-layout">
            <!-- Negative section -->
            <div class="legend-column">
              <div class="section-title">negative</div>
              <div class="legend-item" v-for="(item, index) in negativeItems" :key="'neg-' + index">
                <div class="color-box" :style="{ backgroundColor: item.color }"></div>
                <div class="range-label">{{ item.label }}</div>
              </div>
            </div>
            
            <!-- Positive section -->
            <div class="legend-column">
              <div class="section-title">positive</div>
              <div class="legend-item" v-for="(item, index) in positiveItems" :key="'pos-' + index">
                <div class="color-box" :style="{ backgroundColor: item.color }"></div>
                <div class="range-label">{{ item.label }}</div>
              </div>
            </div>
          </div>
          
          <!-- No data section - full width at bottom -->
          <div class="no-data-section">
            <div class="legend-item">
              <div class="color-box" :style="{ backgroundColor: '#808080' }"></div>
              <div class="range-label">No Data</div>
            </div>
          </div>
        </div>

        <!-- A-E类别图例 -->
        <div v-else class="legend-items category-items">
          <div class="legend-item" v-for="(category, index) in categoryColors" :key="index">
            <div class="color-box" :style="{ backgroundColor: category.color }"></div>
            <div class="range-label">{{ category.code }}</div>
          </div>
        </div>
      </div>
      
      <!-- 城市悬停弹窗 -->
      <div v-if="hoveredCity" class="city-hover-popup" :style="hoveredCityPosition">
        <h3 v-if="legendMode === 'priority'">{{ hoveredCity.name }}({{ currentSelectedValue }})</h3>
        <h3 v-else>{{ hoveredCity.name }}({{ selectedSSPForAE }}-{{ currentSelectedValue }})</h3>
        <div v-if="legendMode === 'priority'" class="hover-info-item">
          <span class="hover-label">Priority:</span>
          <span class="hover-value" :style="{ color: hoveredCity.data.priorityDirection === 'positive' ? '#1976D2' : '#D32F2F' }">
            {{ getDisplayName(hoveredCity.data.priorityValue) }} ({{ hoveredCity.data.priorityDirection }})
          </span>
        </div>
        
        <!-- Priority模式：显示四个变量的数据 -->
        <template v-if="legendMode === 'priority'">
        <div class="hover-info-item">
            <span class="hover-label">UBGS ratio:</span>
            <span class="hover-value" style="color: #333;">
              {{ hoveredCity.data.factors?.x2_urArea?.toFixed(3) || 'N/A' }}
            </span>
          </div>
        <div class="hover-info-item">
            <span class="hover-label">Urban population:</span>
            <span class="hover-value" style="color: #333;">
              {{ hoveredCity.data.factors?.urban_pop?.toFixed(3) || 'N/A' }}
            </span>
          </div>
          <div class="hover-info-item">
            <span class="hover-label">Over threshold:</span>
            <span class="hover-value" style="color: #333;">
              {{ hoveredCity.data.factors?.over_thre?.toFixed(3) || 'N/A' }}
            </span>
          </div>
          <div class="hover-info-item">
            <span class="hover-label">MESH:</span>
            <span class="hover-value" style="color: #333;">
              {{ hoveredCity.data.factors?.MESH_All?.toFixed(3) || 'N/A' }}
            </span>
          </div>
        </template>
        
        <!-- A-E模式：显示Disparity和Accessibility -->
        <template v-else>
          <div class="hover-info-item">
            <span class="hover-label">Disparity:</span>
            <span class="hover-value">{{ hoveredCity.data.disparity }}</span>
          </div>
          <div class="hover-info-item">
            <span class="hover-label">Accessibility:</span>
            <span class="hover-value">{{ hoveredCity.data.accessibility }}</span>
          </div>
          <!-- 在Base Period模式下显示wellbeing值 -->
          <div class="hover-info-item" v-if="selectedSSPForAE === 'base' && hoveredCity.wellbeing !== undefined">
            <span class="hover-label">Wellbeing:</span>
            <span class="hover-value">{{ hoveredCity.wellbeing }}</span>
          </div>
        </template>
      </div>
    </div>

    <!-- 城市数据卡片 -->
    <PriorityChart 
      v-if="selectedCity && selectedCity.data && legendMode === 'priority'"
      :cityData="selectedCity.data" 
      :cityName="selectedCity.name"
      :currentSelectedValue="currentSelectedValue"
      @close="closeDataCard"
    />

    <!-- 双击提示 -->
    <div v-if="showDoubleClickTip" class="double-click-tip" :style="doubleClickTipPosition">
      <div class="tip-content">Double-click to view details</div>
      <div class="tip-arrow"></div>
    </div>

    <!-- 功能介绍弹窗 -->
    <LegendInfo :show="showInfoModal" :mode="legendMode" @close="showInfoModal = false" />
  </div>
</template>

<script>
import { ref, reactive, onMounted, onBeforeUnmount, nextTick, computed, Transition } from 'vue';
import { LMap, LTileLayer, LGeoJson } from '@vue-leaflet/vue-leaflet';
import L from 'leaflet';
import PriorityChart from '../components/PriorityChart.vue';
import LegendInfo from '../components/LegendInfo.vue';
import { generateCityDisplayData } from '../utils/priorityData';
import excelDataLoader, { initializeExcelData, getScenarioData, getYearQuadrantData, getYearSSPQuadrantData, getCityQuadrant, getCityAccessibilityData, getCityYearAccessibilityData, getCityWellbeing } from '../utils/excelDataLoader';
import { useRouter, useRoute } from 'vue-router';
import IntroPanel from '../components/IntroPanel.vue';

export default {
  name: 'Map',
  components: {
    LMap,
    LTileLayer,
    LGeoJson,
    PriorityChart,
    LegendInfo,
    Transition,
    IntroPanel
  },
  setup() {
    // 路由相关
    const router = useRouter();
    const route = useRoute();
    
    // 基础数据定义
    const chinaGeoData = ref(null);
    const priorityData = ref(null);
    const selectedCity = ref(null);
    const searchTerm = ref('');
    const suggestions = ref([]);
    const highlightedCity = ref(null);
    const highlightedIndex = ref(0);
    const legendMode = ref('priority');  // 默认为priority模式
    const selectedSSP = ref('SSP2');     // Priority模式的默认SSP选择
    const selectedYear = ref('2030');    // 设置默认年份为2030
    const excelDataLoaded = ref(false);  // Excel数据加载状态
    
    // 悬停城市数据
    const hoveredCity = ref(null);
    const hoveredCityPosition = ref({ top: '0px', left: '0px' });
    
    // 双击提示相关状态
    const showDoubleClickTip = ref(false);
    const doubleClickTipPosition = ref({ top: '0px', left: '0px' });
    
    // 引用变量
    const mapRef = ref(null);
    const geoJsonLayerRef = ref(null);
    const leafletMap = ref(null);
    const selectedLayer = ref(null);

    // 状态
    const selectedSSPForAE = ref('SSP1');  // A-E模式下的SSP选择
    
    // 双击提示位置更新防抖计时器
    let tipPositionUpdateTimer = null;
    
    // 功能介绍弹窗状态
    const showInfoModal = ref(false);
    
    // 通用函数：检查地图是否准备好
    const isMapReady = () => {
      return leafletMap.value && 
             leafletMap.value._container && 
             leafletMap.value._container.offsetWidth > 0 && 
             leafletMap.value._container.offsetHeight > 0;
    };
    
    // 将原始变量名转换为友好的显示名称
    const getDisplayName = (variableName) => {
      const nameMap = {
        'urban_pop': 'Urban population',
        'MESH_All': 'MESH',
        'x2_urArea': 'UBGS ratio',
        'over_thre': 'Over threshold'
      };
      return nameMap[variableName] || variableName;
    };
    
    // 设置GeoJSON要素的样式
    const getFeatureStyle = (feature) => {
      if (!feature || !feature.properties) return {};
      
      const gbCode = feature.properties.gb?.slice(-6);
      const cityData = priorityData.value && priorityData.value[gbCode];
      // 只有当前选中的城市高亮显示
      const isHighlighted = gbCode === highlightedCity.value;

      // 根据当前模式和城市数据设置填充颜色
      let fillColor = '#d3d3d3';  // 默认灰色
      
      if (cityData) {
        if (isHighlighted) {
          // 根据当前模式设置不同的高亮颜色
          if (legendMode.value === 'category') {
            // A-E模式：使用更深的象限颜色作为高亮
            const categoryColor = cityData.categoryColor;
            if (categoryColor === '#F8FB96') fillColor = '#E6E55B'; // LL - 更深的黄色
            else if (categoryColor === '#92C67C') fillColor = '#7BB05C'; // HL - 更深的绿色
            else if (categoryColor === '#E6BB84') fillColor = '#D4A05C'; // LH - 更深的橙色
            else if (categoryColor === '#E8BEFF') fillColor = '#D09EE6'; // HH - 更深的紫色
            else fillColor = '#666666'; // 默认深灰色
          } else {
            // Priority模式：根据priority direction设置红蓝色
          if (cityData.priorityDirection === 'positive') {
            fillColor = '#1976D2';  // 深蓝色高亮 - positive
          } else {
            fillColor = '#D32F2F';  // 深红色高亮 - negative
            }
          }
        } else if (legendMode.value === 'category') {
          fillColor = cityData.categoryColor || '#d3d3d3';
        } else {
          fillColor = cityData.priorityColor || '#ff6666';
        }
      }

      // 根据城市类型和状态设置不同的样式
      return {
        fillColor: fillColor,
        weight: isHighlighted ? 2 : 0.5,
        opacity: 1,
        color: isHighlighted ? '#666' : '#fff',
        
        dashArray: isHighlighted ? '' : '',
        fillOpacity: isHighlighted ? 0.7 : 0.6
      };
    };
    
    // 更新双击提示位置
    const updateDoubleClickTipPosition = () => {
      if (!selectedLayer.value) return;
      
      try {
        // 使用辅助函数检查地图是否准备好
        if (!isMapReady()) {
          console.warn('地图容器尚未准备好，跳过位置更新');
          return;
        }
        
        // 获取城市边界
        const bounds = selectedLayer.value.getBounds();
        if (!bounds || !bounds.isValid()) {
          console.warn('城市边界无效，跳过位置更新');
          return;
        }
        
        // 获取边界的最北端（最上方）中心点
        const north = bounds.getNorth();
        const centerLng = bounds.getCenter().lng;
        const topCenter = L.latLng(north, centerLng);
        
        // 确保坐标有效
        if (!topCenter || isNaN(topCenter.lat) || isNaN(topCenter.lng)) {
          console.warn('城市坐标无效，跳过位置更新');
          return;
        }
        
        // 将地理坐标转换为屏幕坐标
        const point = leafletMap.value.latLngToContainerPoint(topCenter);
        
        // 确保屏幕坐标有效
        if (!point || isNaN(point.x) || isNaN(point.y)) {
          console.warn('屏幕坐标转换失败，跳过位置更新');
          return;
        }
        
        // 更新提示位置
        doubleClickTipPosition.value = {
          left: `${point.x}px`,
          top: `${point.y + 10}px`
        };
      } catch (error) {
        console.warn('更新双击提示位置失败:', error);
      }
    };
    
    // 通用防抖函数
    const debounce = (fn, delay) => {
      let timer = null;
      return function(...args) {
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => {
          fn.apply(this, args);
          timer = null;
        }, delay);
      };
    };
    
    // 使用防抖包装的更新提示位置函数
    const debouncedUpdateTipPosition = debounce(updateDoubleClickTipPosition, 50);
    
    // 通用函数：重置城市选择状态
    const resetSelection = () => {
      selectedCity.value = null;
      highlightedCity.value = null;
      selectedLayer.value = null;
      
      // 隐藏双击提示
      showDoubleClickTip.value = false;
      
      // 重置所有图层样式
      if (geoJsonLayerRef.value?.leafletObject) {
        geoJsonLayerRef.value.leafletObject.eachLayer(l => {
          if (l.feature) {
            l.setStyle(getFeatureStyle(l.feature));
          }
        });
      }
    };
    
    // 动态选择器选项，根据当前模式显示不同选项
    const sspOptions = computed(() => {
      if (legendMode.value === 'priority') {
        return [
      { value: 'SSP1', label: 'SSP1' },
      { value: 'SSP2', label: 'SSP2' },
      { value: 'SSP3', label: 'SSP3' },
      { value: 'SSP4', label: 'SSP4' },
      { value: 'SSP5', label: 'SSP5' }
    ];
      } else {
        return [
          { value: '2000', label: '2000' },
          { value: '2005', label: '2005' },
          { value: '2010', label: '2010' },
          { value: '2015', label: '2015' },
          { value: '2020', label: '2020' }
        ];
      }
    });

    // SSP选项（用于A-E模式）
    const sspOptionsForAE = computed(() => [
      { value: 'SSP1', label: 'SSP1' },
      { value: 'SSP2', label: 'SSP2' },
      { value: 'SSP3', label: 'SSP3' },
      { value: 'SSP4', label: 'SSP4' },
      { value: 'SSP5', label: 'SSP5' }
    ]);

    // 当前选中的值（根据模式返回不同的值）
    const currentSelectedValue = computed({
      get() {
        if (legendMode.value === 'priority') {
          return selectedSSP.value;
        } else {
          // A-E模式下，根据selectedSSPForAE返回对应的年份
          return selectedYear.value;
        }
      },
      set(value) {
        if (legendMode.value === 'priority') {
          selectedSSP.value = value;
        } else {
          selectedYear.value = value;
        }
      }
    });

    // 图例数据
    const negativeItems = [
      { color: 'rgb(240, 62, 62)', label: 'UBGS ratio' },
      { color: 'rgb(255, 107, 107)', label: 'Urban population' },
      { color: 'rgb(255, 182, 182)', label: 'Over threshold' },
      { color: 'rgb(255, 223, 223)', label: 'MESH' }
    ];

    const positiveItems = [
      { color: '#1E88E5', label: 'UBGS ratio' },
      { color: '#42A5F5', label: 'Urban population' },
      { color: '#64B5F6', label: 'Over threshold' },
      { color: '#90CAF9', label: 'MESH' }
    ];

    const categoryColors = [
      { color: '#F8FB96', code: 'Low accessibility-Low equality' },
      { color: '#92C67C', code: 'High accessibility-Low equality' },
      { color: '#E6BB84', code: 'Low accessibility-High equality' },
      { color: '#E8BEFF', code: 'High accessibility-High equality' }
    ];

    // 单击计时器 - 仅用于双击检测
    let clickTimer = null;
    const clickDelay = 250; // 250ms延迟来区分单击和双击
    
    // 添加全局未处理Promise拒绝的错误处理
    const handleUnhandledRejection = (event) => {
      console.error('未处理的Promise拒绝:', event.reason);
      event.preventDefault(); // 阻止默认的错误处理
    };
    
    // 组件卸载时移除监听器
    onBeforeUnmount(() => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    });
    
    // 加载地图数据
    onMounted(async () => {
      // 监听未处理的Promise拒绝
      window.addEventListener('unhandledrejection', handleUnhandledRejection);
      
      try {
        console.log('开始加载所有数据...');
        
        // 检查路由查询参数，如果有mode=category则切换到A-E模式
        if (route.query.mode === 'category') {
          legendMode.value = 'category';
          console.log('从查询参数检测到category模式，自动切换到A-E页面');
          
          // 恢复SSP和年份设置
          if (route.query.ssp) {
            selectedSSPForAE.value = route.query.ssp;
          }
          if (route.query.year) {
            selectedYear.value = route.query.year;
          }
          
          // 如果没有城市信息需要恢复，立即清除查询参数
          if (!route.query.cityCode) {
            router.replace({ path: '/' }).catch(err => {
              console.warn('路由替换错误:', err);
            });
          }
        }
        
        // 并行加载地理数据和Excel数据
        const [geoRes, excelLoadSuccess] = await Promise.all([
          fetch('/data/geo/updated_data.json'),
          initializeExcelData()
        ]).catch(err => {
          console.error('加载数据失败:', err);
          throw err;
        });
        
        // 处理地理数据
        if (!geoRes) {
          throw new Error('地理数据加载失败');
        }
        chinaGeoData.value = await geoRes.json();
        
        // 设置Excel数据加载状态
        excelDataLoaded.value = excelLoadSuccess;
        
        if (excelLoadSuccess) {
          console.log('Excel数据加载成功，记录数量:', excelDataLoader.getRecordCount());
          console.log('可用scenarios:', excelDataLoader.getAvailableScenarios());
          
          // 根据当前模式生成对应的数据
          if (legendMode.value === 'category') {
            // A-E模式：使用当前选择的SSP和年份
            priorityData.value = generatePriorityDataFromExcel(selectedSSPForAE.value, selectedYear.value);
          } else {
            // Priority模式：使用固定的SSP2作为priority数据源
            priorityData.value = generatePriorityDataFromExcel('SSP2', '2010');
          }
        } else {
          console.error('Excel数据加载失败');
          priorityData.value = {};
        }
        
        // 数据加载后确保样式更新
        if (leafletMap.value) {
          nextTick(() => {
            refreshMapStyles();
            
            // 恢复城市选择状态（如果有的话）
            if (route.query.cityCode && route.query.cityName) {
              const cityCode = route.query.cityCode;
              const cityName = route.query.cityName;
              const cityData = priorityData.value?.[cityCode];
              
              if (cityData) {
                selectedCity.value = {
                  gbCode: cityCode,
                  name: cityName,
                  data: cityData
                };
                highlightedCity.value = cityCode;
                console.log('恢复城市选择状态:', cityName);
                
                // 立即清除URL查询参数，避免刷新时保持状态
                router.replace({ path: '/' }).catch(err => {
                  console.warn('路由替换错误:', err);
                });
                
                // 延迟刷新样式，确保选中状态正确显示
                setTimeout(() => {
                  refreshMapStyles();
                  
                  // 找到对应的图层并设置为选中状态
                  const geoJsonLayer = geoJsonLayerRef.value?.leafletObject;
                  if (geoJsonLayer) {
                    geoJsonLayer.eachLayer(layer => {
                      const feature = layer.feature;
                      if (feature && feature.properties.gb?.slice(-6) === cityCode) {
                        selectedLayer.value = layer;
                        layer.bringToFront();
                        
                        // 调整地图视图到选中的城市
                        if (leafletMap.value && leafletMap.value._container && layer.getBounds) {
                          try {
                            // 确保地图容器已准备好
                            if (leafletMap.value._container.offsetWidth > 0 && leafletMap.value._container.offsetHeight > 0) {
                              leafletMap.value.fitBounds(layer.getBounds(), {
                                padding: [150, 150],
                                maxZoom: 6
                              });
                              
                              // 在地图视图调整后，延迟显示双击提示以确保位置正确
                              if (legendMode.value === 'category') {
                                setTimeout(() => {
                                  showDoubleClickTipForCity(cityName);
                                }, 150); // 增加延迟时间到150ms，确保地图视图调整完成
                              }
                            } else {
                              // 如果地图容器还没准备好，延迟执行
                              setTimeout(() => {
                                if (leafletMap.value && leafletMap.value._container && layer.getBounds) {
                                  try {
                                    leafletMap.value.fitBounds(layer.getBounds(), {
                                      padding: [150, 150],
                                      maxZoom: 6
                                    });
                                  } catch (error) {
                                    console.warn('延迟地图视图调整失败:', error);
                                  }
                                }
                                if (legendMode.value === 'category') {
                                  showDoubleClickTipForCity(cityName);
                                }
                              }, 200);
                            }
                          } catch (error) {
                            console.warn('地图视图调整失败:', error);
                            // 即使地图调整失败，也要显示双击提示
                            if (legendMode.value === 'category') {
                              showDoubleClickTipForCity(cityName);
                            }
                          }
                        }
                      }
                    });
                  }
                }, 100);
              }
            }
          });
        }
      } catch (error) {
        console.error('加载数据失败:', error);
      }
    });

    // 从Excel数据生成priority数据
    const generatePriorityDataFromExcel = (scenario, year) => {
      let scenarioData;
      if (scenario === 'base') {
        // 基期模式：使用SSP2的数据
        scenarioData = getScenarioData('SSP2');
      } else {
        // SSP模式：使用选定的SSP数据
        scenarioData = getScenarioData(scenario);
      }
      
      // 根据不同模式获取象限数据
      let quadrantData;
      if (scenario === 'base') {
        // 基期模式：使用基期象限数据
        quadrantData = getYearQuadrantData(year);
      } else {
        // SSP模式：使用SSP象限数据
        quadrantData = getYearSSPQuadrantData(year, scenario);
      }
      
      const priorityDataMap = {};
      
      console.log(`为scenario ${scenario} 生成priority数据，城市数量:`, Object.keys(scenarioData).length);
      console.log(`为year ${year} 获取象限数据，城市数量:`, Object.keys(quadrantData).length);
      
      for (const [cityCode, excelData] of Object.entries(scenarioData)) {
        const quadrant = legendMode.value === 'category' ? (quadrantData[cityCode] || null) : null;
        
        // 获取accessibility和disparity数据
        let accessibilityData = null;
        if (legendMode.value === 'priority') {
          // Priority模式：使用SSP数据（不区分年份）
          accessibilityData = getCityAccessibilityData(cityCode, scenario);
        } else {
          // A-E模式：根据是否为基期使用不同的数据源
          if (scenario === 'base') {
            accessibilityData = getCityYearAccessibilityData(cityCode, year);
          } else {
            // SSP模式：传递年份参数
            accessibilityData = getCityAccessibilityData(cityCode, scenario, year);
          }
        }
        
        const displayData = generateCityDisplayData(excelData, quadrant, scenario, accessibilityData);
        if (displayData) {
          priorityDataMap[cityCode] = displayData;
        }
      }
      
      return priorityDataMap;
    };

    // 地图准备完成的回调
    const onMapReady = (mapInstance) => {
      leafletMap.value = mapInstance;
      
      // 禁用双击缩放，避免与城市双击事件冲突
      mapInstance.doubleClickZoom.disable();
      
      // 添加地图点击事件监听，处理点击空白区域取消选中
      mapInstance.on('click', (e) => {
        // 延迟执行，让城市点击事件先处理
        setTimeout(() => {
          // 检查是否点击了城市（通过事件标记）
          if (!e.originalEvent.cityClicked && selectedCity.value) {
            console.log('点击空白区域，取消选中城市:', selectedCity.value.name);
            
            // 清除单击计时器
            if (clickTimer) {
              clearTimeout(clickTimer);
              clickTimer = null;
            }
            
            resetSelection();
          }
          
          // 重置城市点击标记
          if (e.originalEvent) {
            e.originalEvent.cityClicked = false;
          }
        }, 300); // 增加延迟时间到300ms，确保城市点击事件（250ms）能够先执行
      });
      
      // 添加地图视图变化监听，更新双击提示位置
      mapInstance.on('zoomend moveend', () => {
        if (showDoubleClickTip.value && selectedLayer.value && selectedCity.value) {
          // 重新计算提示位置
          updateDoubleClickTipPosition();
        }
      });
      
      // 添加更多地图事件监听，确保提示位置实时更新
      mapInstance.on('zoom move', () => {
        if (showDoubleClickTip.value && selectedLayer.value && selectedCity.value) {
          // 在缩放和移动过程中也更新位置，使用防抖版本
          debouncedUpdateTipPosition();
        }
      });
      
      // 在地图准备好后应用样式
      if (geoJsonLayerRef.value && chinaGeoData.value) {
        refreshMapStyles();
      }
    };
    
    // 通用地图视图调整函数
    const adjustMapView = (bounds, options = {}) => {
      if (!bounds) return false;
      
      try {
        // 使用辅助函数检查地图是否准备好
        if (!isMapReady()) {
          console.warn('地图容器尚未准备好，跳过视图调整');
          return false;
        }
        
        const defaultOptions = {
          padding: [150, 150],
          maxZoom: 6
        };
        
        leafletMap.value.fitBounds(bounds, { ...defaultOptions, ...options });
        return true;
      } catch (error) {
        console.warn('地图视图调整失败:', error);
        return false;
      }
    };

    // 刷新地图样式
    const refreshMapStyles = () => {
      const geoJsonLayer = geoJsonLayerRef.value?.leafletObject;
      if (geoJsonLayer && chinaGeoData.value) {
        geoJsonLayer.eachLayer(layer => {
          layer.feature && layer.setStyle(getFeatureStyle(layer.feature));
        });
      }
    };

    // 处理图例标签切换
    const setActiveTab = (tab) => {
      legendMode.value = tab;
      
      // 切换模式时设置默认值
      if (tab === 'category') {
        // 如果切换到A-E模式，根据当前SSP设置年份
        if (selectedSSPForAE.value === 'base') {
          // 基期模式：检查当前年份是否在基期范围内
          const currentYear = parseInt(selectedYear.value);
          if (currentYear < 2000 || currentYear > 2020) {
            selectedYear.value = '2000';  // 不在基期范围内才重置
          }
        } else {
          // SSP模式：检查当前年份是否在SSP范围内
          const currentYear = parseInt(selectedYear.value);
          if (currentYear < 2030 || currentYear > 2100) {
            selectedYear.value = '2030';  // 不在SSP范围内才重置
          }
        }
      }
      
      // 切换模式时重新生成数据
      if (excelDataLoaded.value) {
        const scenario = legendMode.value === 'priority' ? selectedSSP.value : selectedSSPForAE.value;
        const year = legendMode.value === 'category' ? selectedYear.value : '2010';
        priorityData.value = generatePriorityDataFromExcel(scenario, year);
      }
      
      refreshMapStyles();
    };

    // 处理选择器变更
    const handleSSPChange = () => {
      console.log(`${legendMode.value === 'priority' ? 'SSP' : 'Year'} changed to: ${currentSelectedValue.value}`);
      
      if (excelDataLoaded.value) {
        let scenario, year;
        
        if (legendMode.value === 'priority') {
          scenario = selectedSSP.value;
          year = '2010'; // Priority模式使用默认年份
        } else {
          // A-E模式：使用当前选择的SSP和年份
          scenario = selectedSSPForAE.value;
          year = selectedYear.value;
        }
        
        // 重新生成数据
        priorityData.value = generatePriorityDataFromExcel(scenario, year);
        
        // 刷新地图样式
        refreshMapStyles();
        
        // 如果有选中城市，需要更新其数据
        if (selectedCity.value) {
          const cityCode = selectedCity.value.gbCode;
          const cityData = priorityData.value[cityCode];
          if (cityData) {
            selectedCity.value.data = cityData;
          } else {
            // 如果当前场景下没有该城市数据，清除选中状态
            selectedCity.value = null;
            highlightedCity.value = null;
          }
        }
      }
    };

    // 处理A-E模式下的SSP变更
    const handleSSPChangeForAE = () => {
      console.log('A-E mode SSP changed to:', selectedSSPForAE.value);
      
      // 根据选择的SSP设置年份
      if (selectedSSPForAE.value === 'base') {
        selectedYear.value = '2000';  // 基期默认显示2000年
      } else {
        // SSP1-5模式：如果当前年份不在2030-2100范围内，才设置为2030
        const currentYear = parseInt(selectedYear.value);
        if (currentYear < 2030 || currentYear > 2100) {
          selectedYear.value = '2030';  // 只有当前年份不在SSP范围内时才重置
        }
        // 否则保持当前年份不变
      }
      
      // 更新数据
      if (excelDataLoaded.value) {
        priorityData.value = generatePriorityDataFromExcel(selectedSSPForAE.value, selectedYear.value);
        refreshMapStyles();
      }
    };

    // 处理城市点击事件 - 立即执行，不延时
    const handleCityClick = (e, feature) => {
      try {
        console.log('城市点击事件触发:', feature.properties.Eng_name);
        
        // 标记事件已被城市处理
        if (e.originalEvent) {
          e.originalEvent.cityClicked = true;
        }
        
        const layer = e.target;
        const gbCode = feature.properties.gb?.slice(-6);
        const cityName = feature.properties.Eng_name || 'Unknown City';
        const cityData = priorityData.value?.[gbCode];

        console.log('城市数据:', { gbCode, cityName, hasData: !!cityData });

        if (cityData) {
          console.log('设置选中城市:', cityName);
          
          // 先设置选中状态
          selectedCity.value = {
            gbCode,
            name: cityName,
            data: cityData
          };
          
          // 更新高亮状态
          highlightedCity.value = gbCode;
          selectedLayer.value = layer;
          
          // 重置所有图层样式，包括应用新的高亮
          if (geoJsonLayerRef.value?.leafletObject) {
            geoJsonLayerRef.value.leafletObject.eachLayer(l => {
              if (l.feature) {
                l.setStyle(getFeatureStyle(l.feature));
              }
            });
          }
          
          // 确保当前选中的城市在最前面
          if (layer && layer.bringToFront) {
            layer.bringToFront();
          }

          // 调整地图视图
          if (layer && layer.getBounds) {
            // 先处理双击提示
            handleDoubleClickTipDisplay(cityName);
            
            // 调整地图视图
            const success = adjustMapView(layer.getBounds());
            if (!success) {
              // 如果地图调整失败，延迟重试
              setTimeout(() => {
                adjustMapView(layer.getBounds());
              }, 200);
            }
          } else if (legendMode.value === 'category') {
            // 如果没有地图视图调整，直接显示提示
            showDoubleClickTipForCity(cityName);
          }
        } else {
          // 城市无数据时，主动取消当前选中的城市（类似点击空白区域）
          console.log('点击的城市无数据，取消当前选中的城市');
          
          if (selectedCity.value) {
            console.log('取消选中城市:', selectedCity.value.name);
            
            resetSelection();
          }
        }
      } catch (error) {
        console.error('城市点击处理错误:', error);
      }
    };

    // 处理单击事件（延迟执行，确保不是双击的一部分）
    const handleSingleClick = (e, feature) => {
      // 阻止事件冒泡到地图
      e.originalEvent.stopPropagation();
      
      // 清除之前的计时器
      if (clickTimer) {
        clearTimeout(clickTimer);
        clickTimer = null;
      }
      
      // 设置新的计时器 - 只有这里需要延时来区分双击
      clickTimer = setTimeout(() => {
        handleCityClick(e, feature);
        clickTimer = null;
      }, clickDelay);
    };

    // 处理城市双击事件
    const handleCityDoubleClick = (e, feature) => {
      console.log('城市双击事件触发:', feature.properties.Eng_name);
      
      // 阻止事件冒泡到地图
      e.originalEvent.stopPropagation();
      
      // 清除单击计时器，防止单击事件执行
      if (clickTimer) {
        clearTimeout(clickTimer);
        clickTimer = null;
      }
      
      const gbCode = feature.properties.gb?.slice(-6);
      const cityName = feature.properties.Eng_name || 'Unknown City';
      const cityData = priorityData.value?.[gbCode];
      
      console.log('双击城市数据:', { gbCode, cityName, hasData: !!cityData, mode: legendMode.value });
      
      if (cityData) {
        // 标记事件已被城市处理
        if (e.originalEvent) {
          e.originalEvent.cityClicked = true;
        }
        
        if (legendMode.value === 'category') {
          console.log('准备跳转到详细页面:', cityName);
          
          // 隐藏提示
          showDoubleClickTip.value = false;
          
          // 直接跳转到详细数据页面，捕获可能的错误
          router.push({ 
            name: 'MapView', 
            params: { cityCode: gbCode },
            query: {
              cityName: cityName,
              ssp: selectedSSPForAE.value,
              year: selectedYear.value
            }
          }).catch(err => {
            // 捕获路由导航错误（例如导航到相同路由）
            console.warn('路由跳转错误:', err);
          });
        } else {
          console.log('不在A-E模式，不执行跳转');
        }
      } else {
        console.log('城市没有数据，取消当前选中的城市');
        
        // 双击无数据城市时，取消当前选中的城市
        if (selectedCity.value) {
          console.log('取消选中城市:', selectedCity.value.name);
          
          resetSelection();
        }
        
        // 标记事件已处理
        if (e.originalEvent) {
          e.originalEvent.cityClicked = true;
        }
      }
    };

    // 显示双击提示
    const showDoubleClickTipForCity = (cityName) => {
      // 先更新提示位置，但不显示
      updateDoubleClickTipPosition();
      
      // 使用nextTick确保位置更新后再显示
      nextTick(() => {
        showDoubleClickTip.value = true;
      });
      
      // 8秒后自动隐藏提示
      setTimeout(() => {
        showDoubleClickTip.value = false;
      }, 8000);
    };

    // 鼠标悬停高亮效果
    const highlightFeature = (e) => {
      try {
        const layer = e.target;
        const gbCode = layer.feature.properties.gb?.slice(-6);
        const cityData = priorityData.value?.[gbCode];
        
        // 显示悬停弹窗（不管是否选中）
        if (cityData) {
          // 获取鼠标在地图上的位置
          const point = e.containerPoint;
          
          // 设置弹窗位置
          hoveredCityPosition.value = {
            left: `${point.x + 15}px`,
            top: `${point.y - 20}px`
          };
          
          // 获取wellbeing值（仅在Explore模式且选择了Base Period时）
          let wellbeingValue;
          if (legendMode.value === 'category' && selectedSSPForAE.value === 'base') {
            try {
              wellbeingValue = getCityWellbeing(gbCode, currentSelectedValue.value);
              if (wellbeingValue !== null) {
                wellbeingValue = Number(wellbeingValue).toFixed(3);
              }
            } catch (err) {
              console.warn('获取wellbeing数据失败:', err);
              wellbeingValue = undefined;
            }
          }
          
          // 设置悬停城市数据
          hoveredCity.value = {
            name: layer.feature.properties.Eng_name || 'Unknown City',
            data: cityData,
            wellbeing: wellbeingValue
          };
        }
      } catch (error) {
        console.error('highlightFeature错误:', error);
      }
    };

    // 重置高亮效果
    const resetHighlight = (e) => {
      try {
        const layer = e.target;
        
        // 隐藏悬停弹窗
        hoveredCity.value = null;
      } catch (error) {
        console.error('resetHighlight错误:', error);
      }
    };

    // 为每个GeoJSON要素设置事件处理器
    const onEachFeature = (feature, layer) => {
      layer.on({
        click: (e) => {
          try {
            // 使用单击处理函数（延迟执行）
            handleSingleClick(e, feature);
          } catch (error) {
            console.error('点击事件错误:', error);
          }
        },
        dblclick: (e) => {
          try {
            // 使用双击处理函数
            handleCityDoubleClick(e, feature);
          } catch (error) {
            console.error('双击事件错误:', error);
          }
        },
        mouseover: (e) => {
          try {
            highlightFeature(e);
          } catch (error) {
            console.error('悬停事件错误:', error);
          }
        },
        mouseout: (e) => {
          try {
            resetHighlight(e);
          } catch (error) {
            console.error('移出事件错误:', error);
          }
        },
        mousemove: (e) => {
          try {
            // 更新弹窗位置跟随鼠标移动
            if (hoveredCity.value) {
              const point = e.containerPoint;
              hoveredCityPosition.value = {
                left: `${point.x + 15}px`,
                top: `${point.y - 20}px`
              };
            }
          } catch (error) {
            console.error('鼠标移动事件错误:', error);
          }
        }
      });
    };

    // GeoJSON选项配置 - 移到onEachFeature函数定义之后
    const geoJsonOptions = reactive({
      style: (feature) => getFeatureStyle(feature),
      onEachFeature: onEachFeature
    });
    
    // GeoJSON图层准备好的回调
    const onGeoJsonReady = (geoJsonLayer) => {
      // 确保样式应用到每个图层
      if (geoJsonLayer?.leafletObject) {
        geoJsonLayer.leafletObject.eachLayer(layer => {
          layer.feature && layer.setStyle(getFeatureStyle(layer.feature));
        });
      }
    };

    // 执行搜索
    const handleSearch = () => {
      const searchVal = searchTerm.value.trim().toLowerCase();
      if (!searchVal || !chinaGeoData.value) return;
      
      const city = chinaGeoData.value.features.find(f => {
        const name = f.properties.Eng_name?.toLowerCase() || '';
        const gbCode = f.properties.gb?.toLowerCase() || '';
        return name.includes(searchVal) || gbCode.includes(searchVal);
      });

      if (city) {
        const gbCode = city.properties.gb?.slice(-6);
        const cityData = priorityData.value?.[gbCode];
        
        if (cityData) {
          const geoJsonLayer = L.geoJSON(city);
          selectCity(gbCode, city.properties.Eng_name, cityData, geoJsonLayer.getBounds());
          
          // 将找到的城市名称和代码显示在搜索框中
          searchTerm.value = `${city.properties.Eng_name}(${gbCode})`;
          // 清空建议列表
          suggestions.value = [];
        } else {
          alert('City Found but Not in Study Area');
        }
      } else {
        alert('City Not Found');
      }
    };

    // 处理输入变化，更新建议列表
    const handleInputChange = () => {
      highlightedIndex.value = 0;
      const searchVal = searchTerm.value.trim().toLowerCase();

      if (!searchVal || !chinaGeoData.value) {
        suggestions.value = [];
        return;
      }

      suggestions.value = chinaGeoData.value.features
        .filter(f => {
          const name = f.properties.Eng_name?.toLowerCase() || '';
          const gbCode = f.properties.gb?.toLowerCase() || '';
          return name.includes(searchVal) || gbCode.includes(searchVal);
        })
        .slice(0, 5);
    };

    // 处理建议项点击
    const handleSuggestionClick = (feature) => {
      const gbCode = feature.properties.gb?.slice(-6);
      const cityData = priorityData.value?.[gbCode];
      
      if (cityData) {
        const geoJsonLayer = L.geoJSON(feature);
        selectCity(gbCode, feature.properties.Eng_name, cityData, geoJsonLayer.getBounds());
        
        // 将选中的城市名称和代码显示在搜索框中
        searchTerm.value = `${feature.properties.Eng_name}(${gbCode})`;
        // 清空建议列表
        suggestions.value = [];
      }
    };

    // 处理键盘导航
    const handleKeyDown = (e) => {
      const hasSuggestions = suggestions.value.length > 0;
      
      if (e.key === 'Enter') {
        hasSuggestions 
          ? handleSuggestionClick(suggestions.value[highlightedIndex.value])
          : handleSearch();
      } else if (hasSuggestions) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          highlightedIndex.value = (highlightedIndex.value + 1) % suggestions.value.length;
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          highlightedIndex.value = highlightedIndex.value > 0 
            ? highlightedIndex.value - 1 
            : suggestions.value.length - 1;
        }
      }
    };

    // 匹配名称并分解为高亮部分
    const matchName = (item) => {
      const name = item?.properties?.Eng_name || '';
      const term = searchTerm.value.trim();
      
      if (!term || !name) return { before: '', match: '', after: name };
      
      const matchIndex = name.toLowerCase().indexOf(term.toLowerCase());
      if (matchIndex < 0) return { before: '', match: '', after: name };
      
      return {
        before: name.substring(0, matchIndex),
        match: name.substring(matchIndex, matchIndex + term.length),
        after: name.substring(matchIndex + term.length)
      };
    };

    // 关闭数据卡片
    const closeDataCard = () => {
      selectedCity.value = null;
    };

    // 通用城市选择函数
    const selectCity = (gbCode, cityName, cityData, bounds = null) => {
      // 设置选中状态
      selectedCity.value = {
        gbCode,
        name: cityName,
        data: cityData
      };
      
      // 更新高亮状态
      highlightedCity.value = gbCode;
      
      // 重置所有图层样式
      if (geoJsonLayerRef.value?.leafletObject) {
        geoJsonLayerRef.value.leafletObject.eachLayer(l => {
          if (l.feature) {
            l.setStyle(getFeatureStyle(l.feature));
          }
        });
      }
      
      // 如果提供了边界，调整地图视图
      if (bounds) {
        adjustMapView(bounds, { maxZoom: 5 });
      }
    };

    // 处理双击提示显示的辅助函数
    const handleDoubleClickTipDisplay = (cityName) => {
      if (legendMode.value !== 'category') return;
      
      // 先隐藏提示，避免晃动
      showDoubleClickTip.value = false;
      
      // 确保地图已完成视图调整后再显示提示
      const showTip = () => {
        // 只在地图准备好的情况下显示提示
        if (isMapReady()) {
          showDoubleClickTipForCity(cityName);
        }
      };
      
      // 使用一次性事件监听，确保在地图视图调整完成后显示提示
      leafletMap.value.once('moveend', showTip);
      
      // 备用延迟显示，防止事件未触发
      setTimeout(() => {
        // 移除可能未触发的事件监听
        leafletMap.value.off('moveend', showTip);
        
        // 如果提示尚未显示，则显示
        if (!showDoubleClickTip.value) {
          showTip();
        }
      }, 300);
    };

    // 清除搜索框内容
    const clearSearch = () => {
      searchTerm.value = '';
      suggestions.value = [];
    };

    return {
      // 状态
      chinaGeoData,
      priorityData,
      selectedCity,
      searchTerm,
      suggestions,
      highlightedCity,
      highlightedIndex,
      geoJsonOptions,
      legendMode,
      selectedSSP,
      selectedYear,
      currentSelectedValue,
      hoveredCity,
      hoveredCityPosition,
      showDoubleClickTip,
      doubleClickTipPosition,
      excelDataLoaded,
      sspOptions,
      negativeItems,
      positiveItems,
      categoryColors,
      selectedSSPForAE,
      sspOptionsForAE,
      
      // 引用
      mapRef,
      geoJsonLayerRef,
      
      // 方法
      onMapReady,
      onGeoJsonReady,
      handleCityClick,
      handleSingleClick,
      handleCityDoubleClick,
      showDoubleClickTipForCity,
      updateDoubleClickTipPosition,
      highlightFeature,
      resetHighlight,
      handleSearch,
      handleInputChange,
      handleSuggestionClick,
      handleKeyDown,
      matchName,
      setActiveTab,
      handleSSPChange,
      handleSSPChangeForAE,
      closeDataCard,
      debouncedUpdateTipPosition,
      resetSelection,
      isMapReady,
      getDisplayName,
      
      // 功能介绍弹窗状态
      showInfoModal,
      
      // 清除搜索框内容
      clearSearch
    };
  }
};
</script>

<style scoped>
.app-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 7px 0 0 0;
  background-color: #f5f6fa;
  overflow: visible; /* 允许子元素溢出 */
}

.top-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  width: 100%;
  padding: 0 10px;
  position: relative;
  min-height: 40px; /* 确保有足够高度 */
  z-index: 1000; /* 确保整个顶部控制栏在地图之上 */
  overflow: visible; /* 允许下拉框溢出显示 */
}

/* Logo */
.logo-container {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: 0;
  margin-right: 0px;
  position: relative;
  z-index: 20;
}

.logo-image {
  height: 45px;
  width: auto;
  object-fit: contain;
}

/* 左侧控件样式 */
.left-controls {
  display: flex;


  margin-left: 60px;
  position: absolute;
  z-index: 20;
}

/* 平台标题样式 */
.platform-title {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  font-size: 18px;
  font-weight: 500;
  color: #4d4c4c;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 50%;
  z-index: 10;
}

@media (max-width: 1200px) {
  .platform-title {
    font-size: 14px;
    max-width: 60%;
  }
}

@media (max-width: 900px) {
  .platform-title {
    font-size: 12px;
    max-width: 50%;
  }
}

@media (max-width: 600px) {
  .platform-title {
    font-size: 10px;
    max-width: 40%;
  }
}

/* 右侧控件样式 */
.right-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  flex-wrap: nowrap; /* 防止换行 */
  position: relative;
  z-index: 20;
}

/* 模式选择器样式 */
.mode-selector {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(94, 93, 93, 0.15);
  z-index: 1000;
  border: 1px solid rgba(0,0,0,0.05);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin-left: 0;
}

.selector-tabs {
  display: flex;
}

.tab {
  width: 80px;
  padding: 8px 0;
  cursor: pointer;
  font-weight: 500;
  color: #555;
  background-color: white;
  text-align: center;
  transition: all 0.2s ease;
  border-right: 1px solid #eee;
}

.tab:last-child {
  border-right: none;
}

.tab.active {
  color: white;
  font-weight: 600;
  background-color: #4285f4;
}

.tab:hover:not(.active) {
  background-color: #f5f5f5;
}

/* SSP选择器和年份选择器的共同样式 */
.ssp-selector,
.year-selector {
  z-index: 1000;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  border: 1px solid rgba(0,0,0,0.05);
  margin-left: 0px;
  position: relative;
  flex-shrink: 0; /* 防止被压缩 */
}

.ssp-selector + .ssp-selector,
.ssp-selector + .year-selector,
.year-selector + .ssp-selector {
  margin-left: 5px;
}

.ssp-selector::after,
.year-selector::after {
  content: '▼';
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  pointer-events: none;
  color: #666;
  font-size: 12px;
}

.ssp-select,
.year-select {
  width: 120px;
  padding: 8px 30px 8px 12px;
  border: none;
  background-color: transparent;
  font-size: 14px;
  color: #333;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background: none;
}

.ssp-select:focus,
.year-select:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(66, 133, 244, 0.2);
}

.right-controls {
  display: flex;
  align-items: center;
  margin-right: 10px;
  position: relative; /* 建立定位上下文 */
  z-index: 2000; /* 确保右侧控件在最上层 */
  overflow: visible; /* 允许子元素溢出 */
}

.search-container {
  position: relative;
  width: 240px; /* 固定宽度 */
  margin-right: 5px;
  flex-shrink: 0; /* 防止容器被压缩 */
  z-index: 2000; /* 确保搜索框和下拉菜单在地图之上 */
}

.search-box {
  display: flex;
  position: relative;
  width: 100%;
}

.search-box input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-right: none;
  border-radius: 6px 0 0 6px;
  font-size: 14px;
  width: 140px !important;
  min-width: 140px; /* 确保最小宽度 */
  box-sizing: border-box;
}

.search-box button {
  padding: 8px 12px;
  background-color: #4285f4;
  color: white;
  border: none;
  border-radius: 0 4px 4px 0;
  cursor: pointer;
  font-size: 14px;
}

.search-box .clear-button {
  position: absolute;
  right: 70px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: #666;
  font-size: 18px;
  cursor: pointer;
  padding: 0 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.search-box .clear-button:hover {
  color: #333;
  background-color: rgba(0, 0, 0, 0.05);
}

.suggestions-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  background: white;
  border: 1px solid #ddd;
  border-radius: 0 0 6px 6px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 9999; /* 确保在最顶层显示 */
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  margin-top: 2px; /* 增加一点间距 */
}

.suggestion-item {
  padding: 8px 12px;
  cursor: pointer;
  border-bottom: 1px solid #eee;
}

.suggestion-item:last-child {
  border-bottom: none;
}

.suggestion-item:hover,
.suggestion-item.highlighted {
  background-color: #f1f1f1;
}

.matching-text {
  font-weight: bold;
  color: #1a73e8;
}

.city-code {
  color: #70757a;
  font-size: 14px;
}

.map-container {
  flex: 1;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  margin-top: 0px;
  position: relative;
  z-index: 100;
}

.map {
  height: 100%;
  width: 100%;
}

/* todo */
/* 图例样式 */
.left-legend {
  position: absolute;
  bottom: 40px;
  left: 10px;
  background:rgba(250, 250, 250, 0.74);
  padding: 12px;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  font-size: 18px;
  min-width: 200px;
  width: auto;
  border: 1px solid rgba(0,0,0,0.05);
  height: auto;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.legend-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.legend-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.legend-info-icon {
  cursor: pointer;
  font-size: 16px;
  color: #666;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  background: rgba(255, 255, 255, 0.3);
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.legend-info-icon:hover {
  background: rgba(255, 255, 255, 0.6);
  color: #333;
  transform: scale(1.1);
}

.legend-items {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.two-column-layout {
  display: flex;
  gap: 16px;
}

.legend-column {
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
}

.section-title {
  font-size: 13px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
  text-align: center;
}

.legend-item {
  display: flex;
  align-items: center;
  width: 100%;
  margin-bottom: 0;
  height: 20px;
}

.no-data-section {
  display: flex;
  justify-content: center;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #eee;
}

.category-items {
  margin-top: 0;
  padding: 10px 0 6px;
  justify-content: space-evenly;
}

.category-items .legend-item {
  margin-bottom: 0;
  height: 30px;
}

.color-box {
  width: 14px;
  height: 14px;
  margin-right: 6px;
  border: 1px solid rgba(0,0,0,0.1);
  border-radius: 2px;
  flex-shrink: 0;
}

.range-label {
  font-size: 11px;
  color: #444;
  font-weight: 500;
  flex: 1;
  white-space: nowrap;
}

/* 城市悬停弹窗样式 */
.city-hover-popup {
  position: absolute;
  background: white;
  border-radius: 4px;
  padding: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
  z-index: 1500;
  font-size: 13px;
  min-width: 200px;
  max-width: 280px;
  pointer-events: none; /* 避免干扰地图交互 */
  border: 1px solid rgba(0, 0, 0, 0.1);
  transition: opacity 0.2s ease-in-out;
}

.city-hover-popup h3 {
  margin: 0 0 10px 0;
  font-size: 14px;
  font-weight: 600;
  border-bottom: 1px solid #eee;
  padding-bottom: 6px;
}

.hover-info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
  line-height: 1.3;
}

.hover-info-item:last-child {
  margin-bottom: 0;
}

.hover-label {
  color: #666;
  font-weight: 500;
  font-size: 12px;
  min-width: 80px;
  flex-shrink: 0;
}

.hover-value {
  font-weight: 600;
  font-size: 12px;
  text-align: right;
  flex-grow: 1;
}

/* 双击提示样式 */
.double-click-tip {
  position: absolute;
  background: rgba(250, 250, 250, 0.74);
  border-radius: 6px;
  padding: 0;
 
  z-index: 1500;
  pointer-events: none;
  border: 1px solid rgba(0, 0, 0, 0.1);
  transition: opacity 0.2s ease-in-out, left 0.15s ease-out, top 0.15s ease-out;
  transform: translateX(-50%); /* 水平居中 */
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.tip-content {
  padding: 6px;
  margin: 0;
  font-weight: 500;
  white-space: nowrap;
  color: #555555;
  font-size: 13px;
}

.tip-arrow {
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 6px solid rgba(88, 88, 88, 0.74);
  position: absolute;
  bottom: -9px;
  left: 50%;
  transform: translateX(-50%);
}
</style> 